import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from "react";

/** Una tecla de función de la pantalla actual: su rótulo y qué hace. */
export interface Tecla {
  etiqueta: string;
  accion: () => void;
}

export type TeclaId = "F2" | "F3" | "F4" | "F5" | "F6" | "F8" | "F10" | "Escape";
export type MapaTeclas = Partial<Record<TeclaId, Tecla>>;

/** Orden y rótulo por defecto de la barra, al estilo Dominium (DOS/teclado). */
const ORDEN: { id: TeclaId; nombre: string; defecto?: string }[] = [
  { id: "F2", nombre: "F2", defecto: "Buscar" },
  { id: "F3", nombre: "F3", defecto: "Nuevo" },
  { id: "F4", nombre: "F4" },
  { id: "F5", nombre: "F5", defecto: "Actualizar" },
  { id: "F6", nombre: "F6" },
  { id: "F8", nombre: "F8", defecto: "Imprimir" },
  { id: "F10", nombre: "F10", defecto: "Guardar" },
  { id: "Escape", nombre: "Esc", defecto: "Salir" },
];

interface Ctx {
  ref: MutableRefObject<MapaTeclas>;
  visual: MapaTeclas;
  setVisual: (m: MapaTeclas) => void;
}
const TeclasCtx = createContext<Ctx | null>(null);

export function TeclasProvider({ children }: { children: ReactNode }) {
  const ref = useRef<MapaTeclas>({});
  const [visual, setVisual] = useState<MapaTeclas>({});
  return <TeclasCtx.Provider value={{ ref, visual, setVisual }}>{children}</TeclasCtx.Provider>;
}

function useCtx(): Ctx {
  const ctx = useContext(TeclasCtx);
  if (!ctx) throw new Error("Teclas fuera de <TeclasProvider>");
  return ctx;
}

/**
 * Registra las teclas de función de la pantalla actual (se muestran en la barra
 * inferior y se disparan con el teclado). El `ref` se actualiza en cada render para
 * que la acción dispare siempre la versión más reciente (sin cierres viejos); lo
 * visible sólo cambia cuando cambia el juego de teclas (al cambiar de pantalla).
 */
export function useTeclas(mapa: MapaTeclas) {
  const { ref, setVisual } = useCtx();
  ref.current = mapa;
  const clave = Object.keys(mapa).sort().join(",");
  useEffect(() => {
    setVisual(ref.current);
    return () => {
      ref.current = {};
      setVisual({});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave]);
}

/** Dispara la tecla registrada, si hay. Devuelve true si la consumió. */
export function useDisparadorTeclas() {
  const { ref } = useCtx();
  return (key: string): boolean => {
    const t = ref.current[key as TeclaId];
    if (t) {
      t.accion();
      return true;
    }
    return false;
  };
}

/**
 * Barra de teclas de función (estilo Dominium): franja inferior siempre visible con
 * F1 Ayuda fija y las teclas de la pantalla actual resaltadas; a la derecha, el
 * contexto y el reloj (la línea de estado de un ERP de mostrador).
 */
export function BarraTeclas({
  onAyuda,
  contexto,
  globales,
}: {
  onAyuda: () => void;
  contexto?: ReactNode;
  /** Teclas activas en cualquier pantalla; la pantalla actual las puede sobrescribir. */
  globales?: MapaTeclas;
}) {
  const { visual } = useCtx();
  const chips: {
    nombre: string;
    etiqueta: string;
    activa: boolean;
    accion?: (() => void) | undefined;
  }[] = [
    { nombre: "F1", etiqueta: "Ayuda", activa: true, accion: onAyuda },
    ...ORDEN.map((o) => {
      const t = visual[o.id] ?? globales?.[o.id];
      return {
        nombre: o.nombre,
        etiqueta: t?.etiqueta ?? o.defecto ?? "",
        activa: Boolean(t),
        accion: t?.accion,
      };
    }).filter((c) => c.etiqueta !== ""),
  ];
  return (
    <div
      className="fc-barra-teclas flex items-center gap-1 overflow-x-auto border-t px-3 py-1.5 font-data text-2xs"
      style={{ borderColor: "var(--fc-color-border)", background: "var(--fc-color-surface)" }}
    >
      {chips.map((c) => (
        <button
          key={c.nombre}
          type="button"
          disabled={!c.activa || !c.accion}
          onClick={c.accion}
          className="inline-flex shrink-0 items-center gap-1.5 rounded px-1.5 py-0.5 disabled:cursor-default"
          style={{ opacity: c.activa ? 1 : 0.4 }}
          title={c.activa ? c.etiqueta : "No disponible aquí"}
        >
          <kbd
            className="rounded px-1 font-semibold"
            style={{
              background: c.activa ? "var(--fc-color-action)" : "var(--fc-color-border)",
              color: c.activa ? "var(--fc-color-on-action)" : "var(--fc-color-text-muted)",
            }}
          >
            {c.nombre}
          </kbd>
          <span style={{ color: "var(--fc-color-text)" }}>{c.etiqueta}</span>
        </button>
      ))}
      <div className="ml-auto flex shrink-0 items-center gap-3 pl-3" style={{ color: "var(--fc-color-text-muted)" }}>
        {contexto}
        <Reloj />
      </div>
    </div>
  );
}

function Reloj() {
  const [ahora, setAhora] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setAhora(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const fecha = ahora.toLocaleDateString("es-CO", { weekday: "short", day: "2-digit", month: "short" });
  const hora = ahora.toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  return (
    <span style={{ fontVariantNumeric: "tabular-nums" }}>
      {fecha} · {hora}
    </span>
  );
}
